import { CodeEditor } from '@acrodata/code-editor';
import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy,Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAnchor, MatButton, MatIconAnchor } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTab, MatTabContent, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { languages } from '@codemirror/language-data';

import { AdminComponentBase, PageSimpleContentComponent } from 'app/layout/content';
import { ViewContextService } from 'app/navigation/view-context.service';
import { AuthorizationContext, AuthorizationService } from 'app/shared/authentication';
import { ErrorComponent } from 'app/shared/components/error.component';
import { ConfirmDialogService } from 'app/shared/dialog/confirm-dialog-service';
import { GlobalPermission, Maybe, OrderLine, PhotoUnion, Project, ProjectVersion, StorePermission } from 'app/shared/graph';
import { CopyToClipboardComponent } from 'app/shared/utilities/copy-to-clipboard.component';
import { FromNowPipe } from 'app/shared/utilities/from-now.pipe';

import { DynamicScriptLoaderService } from './dynamic-script-loader.service';
import { ExpireProjectResultDialogComponent } from './expire-project-result-dialog.component';
import { ProjectDownloadingDialogComponent } from './project-downloading-dialog.component';
import { ProjectsService } from './projects.service';

@Component({
    templateUrl: './project-page.component.html',
    styleUrls: ['./project-page.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [PageSimpleContentComponent, ErrorComponent, MatTabGroup, MatTab, MatButton, RouterLink, MatIcon, MatIconAnchor, MatMenuTrigger, MatMenu, MatMenuItem, CopyToClipboardComponent, MatTabLabel, MatTabContent, MatProgressSpinner, FormsModule, DatePipe, CodeEditor, FromNowPipe, MatTooltipModule, MatAnchor]
})
export class ProjectPageComponent extends AdminComponentBase implements OnInit, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly authService = inject(AuthorizationService);
    private readonly projectsService = inject(ProjectsService);
    private readonly dynamicScriptLoader = inject(DynamicScriptLoaderService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly matDialog = inject(MatDialog);
    private readonly viewContextService = inject(ViewContextService);

    projectId: string;
    projectVersionId: number | null;

    project?: Maybe<Project>;
    projectVersion?: Maybe<ProjectVersion>;
    authContext?: AuthorizationContext;
    orderLines?: OrderLine[];
    photos?: PhotoUnion[];
    photosStatus: 'loading' | 'loaded' | 'error' = 'loading';
    photosError?: Error;

    loadHubMinJs: Promise<any>;

    constructor() {
        super();
        this.projectId = this.route.snapshot.params.projectId;
        this.projectVersionId = +this.route.snapshot.params.projectVersionId || null;
        this.loadHubMinJs = this.dynamicScriptLoader.load('mediaclip-hub');
    }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            let projectVersionId = +params.projectVersionId || null;
            if (projectVersionId != this.projectVersionId) {
                this.selectProjectVersion(projectVersionId);
            }
        });

        this.loadProject();
    }

    ngOnDestroy(): void {
        super.unsubscribeSubscriptions();
    }

    selectProjectVersion(projectVersionId?: number | null) {
        if (!this.project) {
            return;
        }

        this.projectVersionId = projectVersionId || null;
        if (projectVersionId) {
            this.projectsService.getProjectVersion(this.projectId, projectVersionId).subscribe({
                next: (version) => {
                    this.projectVersion = version;
                },
                error: (err) => {
                    this.setLoadingError(err);
                }
            });
            this.loadPhotos(projectVersionId);
        } else {
            this.photos = undefined;
            this.photosError = undefined;
            this.photosStatus = 'loaded';
        }
    }

    loadPhotos(projectVersionId: number) {
        this.photos = undefined;
        this.photosError = undefined;
        this.photosStatus = 'loading';
        this.registerSubscription(this.projectsService.getProjectVersionPhotos(this.projectId, projectVersionId).subscribe({
            next: (photos) => {
                this.photos = photos ?? [];
                this.photosStatus = 'loaded';
            },
            error: (err) => {
                this.photosError = err;
                this.photosStatus = 'error';
            }
        }));
    }

    reloadPhotos() {
        if (this.projectVersionId) {
            this.loadPhotos(this.projectVersionId);
        }
    }

    getOrientation(width: number, height: number): string {
        if (width === height) {
            return 'square';
        } else if (width > height) {
            return 'landscape';
        } else {
            return 'portrait';
        }
    }

    getVersionPosition(project: Project, projectVersion: ProjectVersion) {
        return project.versions.findIndex((v) => v.id === projectVersion.id) + 1;
    }

    saveXml(projectVersionId: number, xml: string) {
        const domParser = new DOMParser();
        const doc = domParser.parseFromString(xml, 'application/xml');
        if (doc.querySelector('parsererror')) {
            alert('Errors in the XML document. Please fix any errors and try again.');
            return;
        }
        this.subscribeWithGenericSavingErrorHandling(this.projectsService.updateProjectContent({
            projectVersionId: projectVersionId,
            projectXml: xml
        }));
    }

    download(projectVersionId: number) {
        this.subscribeWithGenericSavingErrorHandling(
            this.projectsService.downloadProjectVersion({
                projectVersionId: projectVersionId
            }),
            result => {
                this.matDialog.open(ProjectDownloadingDialogComponent, {
                    data: {
                        downloadUrl: result.downloadUrl
                    }
                });
            }
        );
    }

    expireProject() {
        this.confirmDialogService.confirm(
            'Expire project',
            'Add this project to yesterday’s project expiration report, for testing expiring project notifications'
        ).subscribe(() => {
            this.subscribeWithGenericSavingErrorHandling(
                this.projectsService.simulateProjectExpiration({ id: this.projectId }),
                result => {
                    this.matDialog.open(ExpireProjectResultDialogComponent, {
                        data: {
                            url: result.expiringProjectsApiUrl
                        }
                    });
                }
            );
        });
    }

    delete(projectId: string) {
        this.confirmDialogService.confirm(
            'Delete project',
            'Do you really want to delete this project?',
            undefined,
            {
                yes: {
                    label: 'Delete',
                    warn: true
                },
                no: {
                    label: 'Cancel'
                }
            }).subscribe(() => {
            this.subscribeWithGenericSavingErrorHandling(this.projectsService.deleteProject({ id: projectId }), () => {
                this.loadProject();
            });
        });
    }

    restore(projectId: string) {
        this.confirmDialogService.confirm(
            'Restore project',
            'Do you really want to restore this project?',
            undefined,
            {
                yes: {
                    label: 'Restore',
                    warn: true
                },
                no: {
                    label: 'Cancel'
                }
            }).subscribe(() => {
            this.subscribeWithGenericSavingErrorHandling(this.projectsService.cancelProjectDeletion({ id: projectId }), () => {
                this.loadProject();
            });
        });
    }

    canManageProject(project: Project) {
        return this.authContext?.hasStorePermissions(project.store.id, project.store.subscriptionId, StorePermission.ManageProjects);
    }

    canDownloadProject(projectVersion: ProjectVersion | null | undefined) {
        if (!projectVersion) {
            return false;
        }
        return this.authContext?.hasGlobalPermissions(GlobalPermission.DownloadProjects);
    }

    canExpireProject() {
        return true;
    }

    canReadStoreUsers(project: Project) {
        return this.authContext?.hasStorePermissions(project.store.id, project.store.subscriptionId, StorePermission.ReadStoreUsers);
    }

    canManagePhotos(project: Project) {
        return this.authContext?.hasStorePermissions(project.store.id, project.store.subscriptionId, StorePermission.ManagePhotos);
    }

    canViewApplicationInsights() {
        return this.authContext?.hasGlobalPermissions(GlobalPermission.ReadApplicationInsightsLogs);
    }

    private loadProject() {
        this.subscribeWithGenericLoadingErrorHandling(this.projectsService.getProject(this.projectId), (project) => {
            this.authService.authorizationContext().subscribe(authContext => this.authContext = authContext);
            this.project = project;
            if (project) {
                this.viewContextService.selectStore(project.storeId);
                if (!this.projectVersionId) {
                    this.selectProjectVersion(project.currentVersionId);
                } else {
                    this.selectProjectVersion(this.projectVersionId);
                }
            }
        });

        this.projectsService.getProjectOrders(this.projectId).subscribe((project) => {
            if (project) {
                this.orderLines = project.orderLines?.sort((a, b) => new Date(b.dateCreatedUtc).getTime() - new Date(a.dateCreatedUtc).getTime());
            } else {
                this.orderLines = undefined;
            }
        });
    }

    invalidateCachedPhoto(projectVersion: ProjectVersion) {
        this.confirmDialogService.confirm(
            'Invalidate photos cache',
            'Do you want to invalidate cache of all external photos in this project?',
            'If the user token to the photo source is not valid anymore this project will not be able to be rendered.'
        ).subscribe(() => {
                this.subscribeWithGenericSavingErrorHandling(
                this.projectsService.invalidateProjectStorageExternalPhotosCache(projectVersion.id),
                () => this.reloadPhotos()
            );
            }
        );
    }

    protected readonly languages = languages;
}
